import { useEffect } from "react";
import { CONVERSATION_MESSAGE_PAGE_SIZE } from "@lumen/shared";
import { api } from "../../api";
import type { DisplayConversation } from "./ChatPage";
import type { ChatPageEffectsContext } from "./ChatContextTypes";
import { conversationFollowTarget } from "./conversationQueueState";
import {
  applyConversationStreamEvent,
  isAbortError,
  reconnectConversationStream,
  streamRequest,
} from "./ChatPage.utils";

/** Observe queue changes and new turns while no foreground stream is attached. */
export function useIdleConversationSync({
  id,
  temporary,
  activeRequest,
  conversationRef,
  generationMessageId,
  setConversation,
  setSending,
  setError,
  setUsageRevision,
  applyConversationSnapshot,
  mergeLatestMessages,
  onAttention,
}: ChatPageEffectsContext) {
  useEffect(() => {
    if (!id || temporary) return;
    let stopped = false;
    let polling = false;
    let follower: AbortController | null = null;
    const snapshotController = new AbortController();
    const poll = async () => {
      if (
        stopped ||
        polling ||
        activeRequest.current ||
        conversationRef.current?.id !== id
      )
        return;
      polling = true;
      try {
        const latest = await api<DisplayConversation>(
          `/conversations/${id}?limit=${CONVERSATION_MESSAGE_PAGE_SIZE}`,
          { signal: snapshotController.signal },
        );
        if (
          stopped ||
          activeRequest.current ||
          conversationRef.current?.id !== id
        )
          return;
        setConversation((current) =>
          current?.id === id ? mergeLatestMessages(current, latest) : current,
        );
        const target = conversationFollowTarget(latest);
        if (!target) return;
        const controller = new AbortController();
        follower = controller;
        activeRequest.current = controller;
        generationMessageId.current = target;
        setSending(true);
        void streamRequest(
          `/conversations/${id}/messages/stream?messageId=${encodeURIComponent(target)}`,
          undefined,
          (event) => {
            applyConversationStreamEvent(event, {
              updateConversation: (update) =>
                setConversation((current) =>
                  current?.id === id ? update(current) : current,
                ),
              setConversation,
              assistantMessageId: generationMessageId,
              applyConversationSnapshot,
              mergeLatestMessages,
              conversationId: id,
            });
            if (event.type === "done" || event.type === "cancelled")
              setUsageRevision((value) => value + 1);
            if (event.type === "error") setError(event.error);
          },
          controller.signal,
          {
            isStale: () => conversationRef.current?.id !== id,
            onAttention,
            reconnect: (assistantId) =>
              reconnectConversationStream(id, assistantId ?? target),
          },
        )
          .catch((reason) => {
            if (
              !stopped &&
              !isAbortError(reason) &&
              conversationRef.current?.id === id
            )
              setError(
                reason instanceof Error
                  ? reason.message
                  : "Could not follow generation",
              );
          })
          .finally(() => {
            if (activeRequest.current === controller) {
              activeRequest.current = null;
              if (!stopped && conversationRef.current?.id === id)
                setSending(false);
            }
            if (follower === controller) follower = null;
          });
      } catch {
        // Keep the current snapshot on a transient read failure and retry.
      } finally {
        polling = false;
      }
    };
    const timer = setInterval(() => {
      void poll();
    }, 1500);
    return () => {
      stopped = true;
      clearInterval(timer);
      snapshotController.abort();
      follower?.abort();
      if (activeRequest.current === follower) activeRequest.current = null;
    };
  }, [
    id,
    temporary,
    activeRequest,
    conversationRef,
    generationMessageId,
    setConversation,
    setSending,
    setError,
    setUsageRevision,
    applyConversationSnapshot,
    mergeLatestMessages,
    onAttention,
  ]);
}
